import {
  WIDGET_DIMENSION_OPTIONS,
  type WidgetDimension,
} from "#app/widgets/dashboards/use-dimension-counts";

/**
 * Title mechanics for the dimension-grouped widgets (`dynamicTitle` on each
 * module — the widget owns title resolution entirely, the chrome just displays
 * what it returns). The rule each resolver implements: a stored `config.title`
 * is honored ONLY when it reads as user-authored — anything the widget family
 * itself has ever generated (any grouping's derived form, past or present
 * spellings, old seeds included) is ignored and re-derived from the live
 * `groupBy`. So a title tracks regrouping until a user renames the widget,
 * and a rename is never overwritten.
 */
const DIMENSION_NOUN: Record<WidgetDimension, string> = Object.fromEntries(
  WIDGET_DIMENSION_OPTIONS.map((o) => [o.value, o.label.toLowerCase()]),
) as Record<WidgetDimension, string>;

/**
 * The one title rule every resolver applies: a stored title is honored only
 * when it reads as user-authored — i.e. the widget's own family (`ownsStored`)
 * doesn't claim it. Anything the widget generated re-derives from the live
 * config, so titles track setting changes until a real rename.
 */
export function resolveStoredTitle(
  stored: unknown,
  ownsStored: (title: string) => boolean,
  derived: string,
): string {
  if (typeof stored === "string" && stored.length > 0 && !ownsStored(stored)) return stored;
  return derived;
}

export function workloadByDimensionTitle(groupBy: WidgetDimension): string {
  return `Workload by ${DIMENSION_NOUN[groupBy] ?? groupBy}`;
}

/** Every title the workload family has ever generated for any grouping — the
 *  current "Workload by <x>" form plus the pre-release seed spellings
 *  ("Workload", "Work by <x>"). */
function isGeneratedWorkloadTitle(title: string): boolean {
  if (title === "Workload") return true;
  return Object.values(DIMENSION_NOUN).some(
    (noun) => title === `Workload by ${noun}` || title === `Work by ${noun}`,
  );
}

/** `dynamicTitle` for dimension-bar / type-mix / dimension-battery. */
export function workloadTitle(stored: unknown, groupBy: WidgetDimension): string {
  return resolveStoredTitle(stored, isGeneratedWorkloadTitle, workloadByDimensionTitle(groupBy));
}

const OVER_TIME_GROUPINGS = ["none", "stage", "type", "workflow"] as const;
type OverTimeGrouping = (typeof OVER_TIME_GROUPINGS)[number];

/** series-line's split vocabulary is its own subset (+ "none" = one total line). */
export function overTimeTitle(groupBy: OverTimeGrouping): string {
  if (groupBy === "none") return "Projects over time";
  return `${groupBy.charAt(0).toUpperCase()}${groupBy.slice(1)} over time`;
}

/** `dynamicTitle` for series-line — same rule over its own title family. */
export function seriesLineTitle(stored: unknown, groupBy: OverTimeGrouping): string {
  return resolveStoredTitle(
    stored,
    (title) => OVER_TIME_GROUPINGS.some((g) => title === overTimeTitle(g)),
    overTimeTitle(groupBy),
  );
}

export function portfolioByDimensionTitle(dimension: WidgetDimension): string {
  return `Portfolio by ${DIMENSION_NOUN[dimension] ?? dimension}`;
}

/** `dynamicTitle` for portfolio-map. Curated preset names ("Workflow map",
 *  "Treemap") are deliberately OUTSIDE the family — they read as authored and
 *  stick, exactly like a user rename. */
export function portfolioTitle(stored: unknown, dimension: WidgetDimension): string {
  return resolveStoredTitle(
    stored,
    (title) =>
      title === "Portfolio map" ||
      Object.values(DIMENSION_NOUN).some((noun) => title === `Portfolio by ${noun}`),
    portfolioByDimensionTitle(dimension),
  );
}
